import Divider from '@material-ui/core/Divider'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import ContactSupport from '@material-ui/icons/ContactSupport'
import clsx from 'clsx'
import React from 'react'
import BorderedIconButton from '../Buttons/BorderedIconButton'
import ContentContainer from '../Containers/ContentContainer'
import { AppHeaderContext } from '../Contexts/AppHeaderContext'
import LowerToolbar from '../Headers/LowerToolbar'
import TopBarTools from '../Headers/TopBarTools'
import DoubleAppToolbar from '../Surfaces/DoubleAppToolbar'
import GridContainer from '../Surfaces/GridContainer'
import GridItem from '../Surfaces/GridItem'

const useStyle = makeStyles((theme: Theme) => ({
    bottomSidebarContents: {
        color: '#fff',
        height: '100%',
    },
    padded: {
        margin: `24px 0 0 24px`,
    },
    unpadded: {
        padding: 0,
    },
    fullHeight: {
        height: '100%',
    },
    divider: {
        backgroundColor: `rgba(255, 255, 255, 0.25)`,
    },
    dividerContainer: {
        marginLeft: -28,
        width: 258,
    },
    borderlessIconButton: {
        borderRadius: 0,
        color: theme.palette.common.white,
    },
    biggerIconButton: {
        minHeight: 60,
        height: 60,
    },
}))

const Layout: React.FC = ({ children }) => {
    const theme = useTheme()
    const classes = useStyle()
    let smScreen = useMediaQuery(theme.breakpoints.up('sm'))

    const [sidebarOpen, setSidebarOpen] = React.useState(!smScreen)
    const contentContainerRef = React.useRef<HTMLDivElement>(null)

    const {
        customComponent,
        pageTitle,
        customTextColor,
        barColor,
        hasLowerBottomToolbar,
        setHasLowerBottomToolbar,
        fullHeight,
        isCustomToolbar,
    } = React.useContext(AppHeaderContext)

    const toggleSidebarOpen = React.useCallback(
        (newValue: boolean | 'toggle') => {
            if (newValue !== 'toggle') setSidebarOpen(newValue)
            else setSidebarOpen(!sidebarOpen)
        },
        [sidebarOpen]
    )

    const upperChildren = React.useMemo(
        () => (
            <TopBarTools
                toggleSidebarOpen={toggleSidebarOpen}
                logoOnly
                logoWithChildren
            >
                {smScreen && (
                    <GridContainer
                        className={clsx(
                            classes.bottomSidebarContents,
                            !smScreen && classes.padded
                        )}
                        direction={smScreen ? 'row' : 'column'}
                        justifyContent="flex-end"
                        alignItems="flex-start"
                    >
                        <GridItem>
                            <BorderedIconButton data-testid="globalNotificationButton">
                                <ContactSupport />
                            </BorderedIconButton>
                        </GridItem>

                        {!smScreen && (
                            <GridItem className={classes.dividerContainer}>
                                <Divider className={classes.divider} />
                            </GridItem>
                        )}
                    </GridContainer>
                )}
            </TopBarTools>
        ),
        [toggleSidebarOpen, smScreen]
    )

    const lowerChildren = React.useMemo(
        () => (
            <LowerToolbar
                customText={pageTitle}
                customTextColor={customTextColor}
                lowerBarBackgroundColor={barColor}
                customComponent={customComponent}
                routes={null}
                setHasLowerBottomToolbar={setHasLowerBottomToolbar}
                hasLowerBottomToolbar={hasLowerBottomToolbar}
                fullHeight={fullHeight}
                isCustomToolbar={isCustomToolbar}
            />
        ),
        [
            pageTitle,
            customTextColor,
            barColor,
            customComponent,
            setHasLowerBottomToolbar,
            hasLowerBottomToolbar,
            fullHeight,
            isCustomToolbar,
        ]
    )

    return (
        <>
            <DoubleAppToolbar
                UpperChildren={upperChildren}
                LowerChildren={lowerChildren}
            />
            <ContentContainer
                top={hasLowerBottomToolbar ? 150 : 100}
                ref={contentContainerRef}
            >
                {children}
            </ContentContainer>
        </>
    )
}

export default Layout
